/* ============================================================================
 * WALLET.EXE — assets, consent and waivers
 * ----------------------------------------------------------------------------
 * "Native asset control combined with legal/compliance tooling — cryptographic
 * signing, consent collection, and zero-knowledge waivers."
 *
 * PAGE, NOT A LIST. This desk used to be a bare `.stack` of `.panel`s: four
 * rows of key/value, then four more, then a form. A wallet that never shows a
 * figure is not a wallet, and a page with no head, no command line and no
 * provenance is not a page. It now carries the skeleton every desk in the OS
 * carries (docs/PAGE-LAYOUT.md), and it leads with the one thing a wallet owes
 * before anything else: the number, and the identity it belongs to.
 *
 * HONEST STATUS:
 *   • Asset control: read-only. The native balance is a real chain read, and
 *     the desk never moves anything.
 *   • Consent: EIP-712 `Consent` records ARE real signatures, and the local
 *     ledger is real. But there is no consent registry contract, so a record is
 *     only as durable as this browser's sessionStorage. The UI says so.
 *   • "Zero-knowledge waivers": NOT IMPLEMENTED. A real ZK waiver needs a
 *     circuit and a verifier contract. Signing a typed message is not
 *     zero-knowledge, and this desk does not claim it is.
 * ========================================================================= */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBalance, useBlockNumber, useWalletClient } from 'wagmi';
import { base } from 'wagmi/chains';
import { formatUnits, type Address, type Hex } from 'viem';
import { useWallet } from '../hooks/useWallet';
import {
  ERC6551_ACCOUNT_IMPLEMENTATION,
  ERC6551_REGISTRY,
  IMMERSION_CHAIN_ID,
  IMMERSION_TOKEN_CONTRACT,
  immersionAccount,
} from '../lib/immersion';
import { SkinArt } from '../components/SkinArt';

const LEDGER_KEY = 'supercompute-webos:consent-ledger';

/** Base mainnet explorer. A wallet without a way to verify itself is a claim. */
const BASESCAN = 'https://basescan.org';

/**
 * Amounts are rendered at a fixed precision, and a non-zero amount that rounds
 * to zero is rendered as `below the render precision` — never as `0.000000`.
 * A wallet that prints a zero it did not read is lying about the balance.
 */
function fmtAmount(value: bigint | undefined, decimals: number, dp: number): string {
  if (value === undefined) return '—';
  if (value === 0n) return (0).toFixed(dp);
  const unit = 10 ** -dp;
  const n = Number(formatUnits(value, decimals));
  if (n === 0) return 'unreadable';
  if (n < unit) return `< ${unit.toFixed(dp)}`;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
}

/* ── Canon-free, clearly-owned EIP-712 consent shape ──────────────────────── */

const CONSENT_DOMAIN = {
  name: 'Supercompute Consent',
  version: '1',
} as const;

const CONSENT_TYPES = {
  Consent: [
    { name: 'documentHash', type: 'bytes32' },
    { name: 'signer', type: 'address' },
    { name: 'scope', type: 'string' },
    { name: 'grantedAt', type: 'uint64' },
    { name: 'expiresAt', type: 'uint64' },
  ],
} as const;

interface ConsentRecord {
  documentHash: Hex;
  scope: string;
  signer: Address;
  grantedAt: number;
  expiresAt: number;
  signature: Hex;
  chainId: number;
}

function loadLedger(): ConsentRecord[] {
  try {
    const raw = sessionStorage.getItem(LEDGER_KEY);
    return raw ? (JSON.parse(raw) as ConsentRecord[]) : [];
  } catch {
    return [];
  }
}

function Row({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="row">
      <span className="row__k">{k}</span>
      <span
        className={`row__v${mono ? ' mono' : ''}`}
        style={mono ? { fontSize: 10 } : undefined}
      >
        {v}
      </span>
    </div>
  );
}

function short(v: string, n = 10): string {
  return v.length > n * 2 + 1 ? `${v.slice(0, n)}…${v.slice(-6)}` : v;
}

export function WalletDesk() {
  const w = useWallet();
  const { data: signerClient } = useWalletClient();

  /* ── live reads ─────────────────────────────────────────────────────────── */
  const { data: bal, isLoading: balLoading, isError: balError } = useBalance({
    address: w.address,
    chainId: base.id,
    query: { enabled: Boolean(w.address) },
  });

  const { data: blockNumber } = useBlockNumber({ chainId: base.id, watch: true });

  /* ── consent ────────────────────────────────────────────────────────────── */
  const [scope, setScope] = useState('terms-of-service');
  const [documentText, setDocumentText] = useState(
    'Supercompute participation terms — v1',
  );
  const [days, setDays] = useState(365);
  const [ledger, setLedger] = useState<ConsentRecord[]>(() => loadLedger());
  const [hash, setHash] = useState<Hex | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(LEDGER_KEY, JSON.stringify(ledger));
  }, [ledger]);

  // Real keccak256 of the document text — the thing actually signed over.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { keccak256, toHex } = await import('viem');
      const bytes = new TextEncoder().encode(documentText);
      const hex = toHex(bytes);
      if (!cancelled) setHash(keccak256(hex));
    })();
    return () => {
      cancelled = true;
    };
  }, [documentText]);

  const grantedAt = useMemo(() => BigInt(Math.floor(Date.now() / 1000)), []);
  const expiresAt = useMemo(
    () => BigInt(Math.floor(Date.now() / 1000) + days * 86400),
    [days],
  );

  /** Preview of the Immersion container this wallet would control. */
  const tba = useMemo(() => {
    if (!w.address) return null;
    try {
      return immersionAccount(IMMERSION_TOKEN_CONTRACT, 0n, IMMERSION_CHAIN_ID);
    } catch {
      return null;
    }
  }, [w.address]);

  const sign = useCallback(async () => {
    setErr(null);
    if (!signerClient || !w.address || !hash) {
      setErr('Connect a wallet and enter a document.');
      return;
    }
    try {
      const sig = await signerClient.signTypedData({
        account: w.address,
        domain: { ...CONSENT_DOMAIN, chainId: base.id },
        types: CONSENT_TYPES,
        primaryType: 'Consent',
        message: {
          documentHash: hash,
          signer: w.address,
          scope,
          grantedAt,
          expiresAt,
        },
      });
      setLedger((prev) => [
        ...prev,
        {
          documentHash: hash,
          scope,
          signer: w.address as Address,
          grantedAt: Number(grantedAt),
          expiresAt: Number(expiresAt),
          signature: sig,
          chainId: base.id,
        },
      ]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [signerClient, w.address, hash, scope, grantedAt, expiresAt]);

  const copyAddress = useCallback(async () => {
    if (!w.address) return;
    try {
      await navigator.clipboard.writeText(w.address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setErr('Clipboard refused — select the address and copy it by hand.');
    }
  }, [w.address]);

  const nativeAmt = bal ? fmtAmount(bal.value, bal.decimals, 6) : null;

  return (
    <div className="dsp page">
      {/* ── masthead ────────────────────────────────────────────────────── */}
      <header className="page__head">
        <SkinArt
          className="page__crest"
          src="/logos/dock-wallet.png"
          width={40}
          height={40}
          alt=""
          aria-hidden="true"
        />
        <div className="page__titles">
          <p className="eyebrow">wallet · asset control</p>
          <h1 className="h1">Sign what you hold</h1>
        </div>
        <div className="page__chips">
          <span className={w.isConnected ? 'badge badge-live' : 'badge badge-claude'}>
            {w.isConnected ? 'connected' : 'no wallet'}
          </span>
          <span className="badge badge-admin">read-only</span>
        </div>
      </header>

      {/* ── command bar ─────────────────────────────────────────────────── */}
      <div className="page__bar">
        <div className="grow">
          <span className="eyebrow">holdings — base</span>
          <span className="micro">
            A live balance read. The desk never moves funds.
          </span>
        </div>
        <div className="hstack">
          {w.isConnected ? (
            <>
              <button type="button" className="btn" onClick={copyAddress}>
                {copied ? 'Address copied' : 'Copy address'}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => w.disconnect()}
              >
                Disconnect
              </button>
            </>
          ) : (
            <button type="button" className="btn" onClick={w.connect}>
              Connect wallet
            </button>
          )}
        </div>
      </div>

      {/* ── the figure, and whose it is ─────────────────────────────────── */}
      <section className="wal-hero" data-od-id="wallet-hero">
        <div className="wal-fig">
          <span className="eyebrow">native balance · base</span>
          <span className="wal-fig__val">
            {!w.address ? (
              <span className="unread">—</span>
            ) : balLoading ? (
              '…'
            ) : balError ? (
              <span className="unread">unread</span>
            ) : (
              <>
                {nativeAmt} <em>{bal?.symbol ?? 'ETH'}</em>
              </>
            )}
          </span>
          <span className="wal-fig__sub">
            {blockNumber
              ? `block ${blockNumber.toLocaleString('en-US')} · read live`
              : 'block unread · the dateline says so rather than guessing'}
          </span>
        </div>

        <div className="wal-id">
          {w.ensAvatar ? (
            <img
              className="wal-id__av"
              src={w.ensAvatar}
              width={36}
              height={36}
              alt=""
              aria-hidden="true"
            />
          ) : (
            <img
              className="wal-id__av"
              src="/logos/supercompute-mark-64.png"
              width={36}
              height={36}
              alt=""
              aria-hidden="true"
            />
          )}
          <div className="wal-id__t">
            <span className="wal-id__handle">
              {w.isConnected ? w.handle : 'GUEST'}
            </span>
            <span className="wal-id__addr">
              {w.address ?? 'no account connected'}
            </span>
          </div>
        </div>
      </section>

      {/* ── the asset ledger ────────────────────────────────────────────── */}
      <div className="page__bar">
        <div className="grow">
          <span className="eyebrow">assets — read-only</span>
          <span className="micro">
            Balances are contract reads; a failed read says <b>unread</b> and
            never falls back to zero.
          </span>
        </div>
        {w.address && (
          <a
            className="chip"
            href={`${BASESCAN}/address/${w.address}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            Verify on BaseScan →
          </a>
        )}
      </div>

      <div className="wal-assets" data-od-id="wallet-assets">
        {/* native */}
        <div className="wal-asset">
          <span className="wal-asset__mark" aria-hidden="true">
            Ξ
          </span>
          <span className="wal-asset__n">
            <span className="wal-asset__name">
              {bal?.symbol ?? 'ETH'} — native
            </span>
            <span className="wal-asset__sub">
              base 8453 · chain balance · gas is paid from here
            </span>
          </span>
          <span className="wal-asset__amt">
            {!w.address
              ? '—'
              : balLoading
                ? '…'
                : balError
                  ? 'unread'
                  : nativeAmt}
            <small>{balLoading || !w.address ? '' : 'available'}</small>
          </span>
        </div>

        {/* the container */}
        <div className="wal-asset">
          <SkinArt
            className="wal-asset__tile"
            src="/logos/dock-embassy.png"
            width={26}
            height={26}
            alt=""
            aria-hidden="true"
          />
          <span className="wal-asset__n">
            <span className="wal-asset__name">Immersion container — ERC-6551</span>
            <span className="wal-asset__sub">
              derived, not deployed · nothing minted to bind
            </span>
          </span>
          <span className="wal-asset__amt">
            0
            <small>not minted</small>
          </span>
        </div>
      </div>

      {/* ── consent: the work, then the standing facts ──────────────────── */}
      <div className="page__grid">
        <div className="stack">
          <div className="page__bar">
            <div className="grow">
              <span className="eyebrow">consent &amp; waivers — EIP-712</span>
              <span className="micro">
                A signature over a document hash is proof of what you agreed to,
                and nothing more.
              </span>
            </div>
          </div>

          <div className="stack" style={{ gap: 4 }}>
            <label className="label" htmlFor="w-doc">
              Document
            </label>
            <textarea
              id="w-doc"
              rows={3}
              className="field"
              value={documentText}
              onChange={(e) => setDocumentText(e.target.value)}
            />
            <label className="label" htmlFor="w-scope">
              Scope
            </label>
            <input
              id="w-scope"
              className="field"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            />
            <label className="label" htmlFor="w-days">
              Valid for (days)
            </label>
            <input
              id="w-days"
              className="field"
              type="number"
              value={days}
              onChange={(e) => setDays(Number(e.target.value) || 0)}
              style={{ maxWidth: 140 }}
            />
          </div>

          <div className="panel">
            <Row k="Domain" v={`${CONSENT_DOMAIN.name} · 1 · ${base.id}`} />
            <Row
              k="documentHash"
              v={hash ?? 'hashing…'}
              mono
            />
            <Row k="grantedAt" v={grantedAt.toString()} />
            <Row
              k="expiresAt"
              v={`${expiresAt.toString()} · ${new Date(Number(expiresAt) * 1000)
                .toISOString()
                .slice(0, 10)}`}
            />
          </div>

          <div className="hstack">
            <button
              type="button"
              className="btn btn--primary"
              onClick={sign}
              disabled={!w.isConnected || !hash}
            >
              Sign consent
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setLedger([])}
              disabled={ledger.length === 0}
            >
              Clear ledger
            </button>
          </div>

          {err && <p className="note note--warn">{err}</p>}

          <p className="note note--warn">
            These are real EIP-712 signatures over a real keccak256 of the
            document. The ledger is <strong>sessionStorage only</strong> — there
            is no consent registry contract, so a record does not survive outside
            this browser.
          </p>
          <p className="note note--warn">
            &quot;Zero-knowledge waivers&quot; are <strong>not implemented</strong>
            . A genuine ZK waiver needs a circuit plus an on-chain verifier. An
            EIP-712 signature is not zero-knowledge; this desk will not describe
            it as such.
          </p>
        </div>

        {/* the rail: standing facts, not work */}
        <aside className="stack">
          <div className="page__bar">
            <div className="grow">
              <span className="eyebrow">immersion container — ERC-6551</span>
            </div>
          </div>
          <div className="panel">
            <Row k="Registry" v={short(ERC6551_REGISTRY, 10)} mono />
            <Row k="Implementation" v={short(ERC6551_ACCOUNT_IMPLEMENTATION, 10)} mono />
            <Row
              k="Derived account"
              v={tba ?? <span className="tag tag--muted">connect a wallet</span>}
              mono
            />
            <Row
              k="Minted"
              v={<span className="tag tag--warn">no — collection not deployed</span>}
            />
          </div>
          <p className="note">
            The address above is a real ERC-6551 derivation, checked against the
            deployed registry. It is a preview: without a deployed Immersion
            collection there is no token for it to bind to, so nothing exists at
            it.
          </p>

          <div className="page__bar">
            <div className="grow">
              <span className="eyebrow">consent ledger — this session</span>
            </div>
            <span className="tag tag--muted">{ledger.length} record(s)</span>
          </div>
          <div className="sheet">
            <div className="sheet-rows">
              {ledger.length === 0 && (
                <div className="lrow">
                  <span className="lrow__t">
                    Empty
                    <span className="sub">nothing signed in this session yet</span>
                  </span>
                </div>
              )}
              {ledger.map((r, i) => (
                <div className="lrow" key={i}>
                  <span className="lrow__t">
                    {r.scope}
                    <span className="sub">
                      {short(r.signer, 6)} · expires{' '}
                      {new Date(r.expiresAt * 1000).toISOString().slice(0, 10)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* ── provenance ──────────────────────────────────────────────────── */}
      <div className="page__status">
        <span>
          base <b>8453</b> · reads only
        </span>
        <span>
          block{' '}
          <b className={blockNumber ? '' : 'unread'}>
            {blockNumber ? blockNumber.toLocaleString('en-US') : 'unread'}
          </b>
        </span>
        <span>
          account <b>{w.shortAddress ?? 'none'}</b>
        </span>
        <span>
          transfers <b>not implemented — this desk never moves funds</b>
        </span>
      </div>
    </div>
  );
}
